import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaPlus, FaTrash, FaChevronDown, FaChevronUp, FaCamera, FaUpload, FaTimes, FaImage, FaBed, FaMoneyBillWave, FaClock, FaConciergeBell, FaScroll, FaMapMarkerAlt, FaLandmark, FaFileContract, FaTags, FaInfoCircle, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getApiUrl } from '../config/api';
import { generatePropertyDescription } from '../services/propertyDescriptionAPI';
import { toast } from 'react-toastify';
import './AddStayPropertyPage.css';

const propertyTypes = [
  'Hotel',
  'Guest House',
  'PG (Paying Guest)',
  'Villa',
  'Apartment',
];

const ALL_AMENITIES = ['WiFi', 'Air Conditioning', 'Heating', 'TV', 'Kitchen', 'Washing Machine', 'Hot Water', 'Bed Linens', 'Refrigerator', 'Microwave', 'Balcony', 'Free Parking', 'Lift/Elevator', 'CCTV', 'Gym', 'Swimming Pool', 'Pets Allowed', 'Dedicated Workspace', 'Room Service', 'Housekeeping', 'Breakfast Included', 'Power Backup', 'Geyser', 'Fan'];
const ALL_HOUSE_RULES = ['Check-in: 2 PM - 10 PM', 'Check-out: 11 AM', 'No Smoking', 'No Pets', 'No Parties/Events', 'No Unregistered Guests', 'Quiet Hours: 10 PM - 8 AM', 'ID Proof Required', 'Damage Charges Apply', 'Security Deposit Required', 'Shoes Off Inside', 'Respect Neighbors'];

const STEPS = [
  { label: 'Basic Info', icon: <FaInfoCircle /> },
  { label: 'Details', icon: <FaBed /> },
  { label: 'Check-in', icon: <FaClock /> },
  { label: 'Pricing & Info', icon: <FaMoneyBillWave /> },
  { label: 'Places', icon: <FaMapMarkerAlt /> },
  { label: 'Policies', icon: <FaFileContract /> },
  { label: 'Offers', icon: <FaTags /> },
];

const CHECK_IN_WINDOWS = [
  '12:00 PM - 10:00 PM',
  '1:00 PM - 11:00 PM',
  '2:00 PM - 10:00 PM',
  '2:00 PM - 11:00 PM',
  '3:00 PM - 11:00 PM',
  '4:00 PM - 11:30 PM',
];

const CHECK_OUT_TIMES = [
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
];

const NEARBY_PLACE_CATEGORIES = [
  'Transport',
  'Restaurant',
  'Hospital',
  'School/College',
  'Shopping',
  'Tourist Attraction',
  'Business Hub',
  'Beach/Lake',
  'Park',
  'Other',
];

const CANCELLATION_POLICY_OPTIONS = [
  'Free cancellation up to 24 hours before check-in',
  'Free cancellation up to 48 hours before check-in',
  'Free cancellation up to 7 days before check-in',
  'Non-refundable',
  'Flexible - Full refund 1 day prior to arrival',
  'Moderate - Full refund 5 days prior to arrival',
  'Strict - 50% refund up to 1 week prior to arrival',
];

const PAYMENT_POLICY_OPTIONS = [
  'Pay at property',
  'Pay online (full amount)',
  'Pay 50% now, 50% at property',
  'Pay online with free cancellation',
  'Advance payment required',
];

const AddStayPropertyPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const touristImageInputRef = useRef(null);
  const stepBarRef = useRef(null);
  const checkInDropdownRef = useRef(null);
  const checkOutDropdownRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [openTimeDropdown, setOpenTimeDropdown] = useState('');
  const [newNearbyPlace, setNewNearbyPlace] = useState({ name: '', category: '', distance: '', unit: 'km' });
  const [newTouristPlace, setNewTouristPlace] = useState({ name: '', distance: '', description: '', image: null });
  const [touristImagePreview, setTouristImagePreview] = useState(null);
  const [showAmenitiesSection, setShowAmenitiesSection] = useState(true);
  const [showHouseRulesSection, setShowHouseRulesSection] = useState(true);
  const [newAmenity, setNewAmenity] = useState('');
  const [newHouseRule, setNewHouseRule] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hotel',
    description: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    maxGuests: 2,
    propertySizeSqFt: '',
    checkInTime: '3:00 PM - 11:00 PM',
    checkOutTime: '11:00 AM',
    minStayNights: 1,
    maxStayNights: '',
    securityDeposit: 0,
    pricePerNight: '',
    cleaningFee: 0,
    serviceFeePercent: 10,
    images: [],
    amenities: [],
    customAmenities: [],
    houseRules: [],
    customHouseRules: [],
    nearbyPlaces: [],
    touristPlaces: [],
    cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
    paymentPolicy: 'Pay at property',
    additionalNotes: '',
    discountIsActive: false,
    discountType: 'percentage',
    discountValue: '',
    discountTitle: '',
    discountDescription: '',
    discountValidFrom: '',
    discountValidUntil: '',
    discountMinStayNights: 1,
    discountBadge: 'Special Offer',
    discountMaxAmount: '',
    discountTermsConditions: '',
  });

  // Manage tourist image preview URL
  useEffect(() => {
    if (newTouristPlace.image) {
      const url = URL.createObjectURL(newTouristPlace.image);
      setTouristImagePreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setTouristImagePreview(null);
    }
  }, [newTouristPlace.image]);

  // Auto-scroll step bar to keep active step visible
  useEffect(() => {
    if (stepBarRef.current) {
      const activeEl = stepBarRef.current.querySelector('.add-stay-step.active');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeStep]);

  useEffect(() => {
    const onOutsideClick = (event) => {
      if (
        checkInDropdownRef.current &&
        checkInDropdownRef.current.contains(event.target)
      ) {
        return;
      }
      if (
        checkOutDropdownRef.current &&
        checkOutDropdownRef.current.contains(event.target)
      ) {
        return;
      }
      setOpenTimeDropdown('');
    };

    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, []);

  const goToStep = (index) => {
    if (index >= 0 && index < STEPS.length) {
      setActiveStep(index);
    }
  };

  const goNext = () => {
    if (activeStep === 0) {
      if (!formData.name.trim()) { toast.error('Please enter a property name.'); return; }
      if (!formData.city.trim()) { toast.error('Please enter a city.'); return; }
    }
    if (activeStep === 3) {
      if (!formData.pricePerNight || Number(formData.pricePerNight) <= 0) { toast.error('Please enter a valid price per night.'); return; }
    }
    if (activeStep < STEPS.length - 1) setActiveStep(activeStep + 1);
  };

  const goPrev = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const sanitizeIntegerInput = (rawValue) => String(rawValue ?? '').replace(/\D/g, '');

  const handleIntegerInputChange = (field, rawValue, { max } = {}) => {
    const digits = sanitizeIntegerInput(rawValue);
    if (digits === '') {
      handleChange(field, '');
      return;
    }
    let parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed)) return;
    if (max != null && parsed > max) parsed = max;
    handleChange(field, parsed);
  };

  const handleIntegerInputBlur = (field, { min = 1, fallback = min } = {}) => {
    const current = formData[field];
    if (current === '' || current === null || current === undefined) {
      handleChange(field, fallback);
      return;
    }
    const parsed = parseInt(current, 10);
    handleChange(field, Number.isFinite(parsed) ? Math.max(min, parsed) : fallback);
  };

  const toInteger = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const handleGenerateDescription = async () => {
    if (!formData.name?.trim() && !formData.city?.trim()) {
      toast.info('Please enter property name or city first.');
      return;
    }

    setGeneratingDescription(true);
    try {
      const result = await generatePropertyDescription({
        propertyName: formData.name,
        propertyType: formData.type,
        city: formData.city,
        state: formData.state,
        country: 'India',
        address: formData.address,
        amenities: formData.amenities,
        customAmenities: formData.customAmenities,
        houseRules: formData.houseRules,
        customHouseRules: formData.customHouseRules,
        nearbyPlaces: formData.nearbyPlaces,
        touristPlaces: formData.touristPlaces,
        bedrooms: toInteger(formData.bedrooms, 1),
        bathrooms: toInteger(formData.bathrooms, 1),
        beds: toInteger(formData.beds, 1),
        maxGuests: toInteger(formData.maxGuests, 2),
        propertySizeSqFt: formData.propertySizeSqFt,
        cancellationPolicy: formData.cancellationPolicy,
        paymentPolicy: formData.paymentPolicy,
        checkInTime: formData.checkInTime,
        checkOutTime: formData.checkOutTime,
        minStayNights: formData.minStayNights,
        additionalNotes: formData.additionalNotes,
        discountIsActive: formData.discountIsActive,
        discountTitle: formData.discountTitle,
      });
      handleChange('description', result.description);
      toast.success('Description generated successfully.');
    } catch (err) {
      toast.error(err.message || 'Could not generate description.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const addNearbyPlace = (place) => {
    if (!place.name || place.distance === '' || place.distance == null) return;
    setFormData(prev => ({
      ...prev,
      nearbyPlaces: [...prev.nearbyPlaces, { name: place.name, category: place.category || '', distance: parseFloat(place.distance) || 0, unit: place.unit || 'km' }]
    }));
  };
  const toggleAmenity = (a) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(a) ? prev.amenities.filter(x => x !== a) : [...prev.amenities, a]
    }));
  };
  const addCustomAmenity = () => {
    if (!newAmenity.trim()) return;
    setFormData(prev => ({ ...prev, customAmenities: [...prev.customAmenities, newAmenity.trim()] }));
    setNewAmenity('');
  };
  const toggleHouseRule = (r) => {
    setFormData(prev => ({
      ...prev,
      houseRules: prev.houseRules.includes(r) ? prev.houseRules.filter(x => x !== r) : [...prev.houseRules, r]
    }));
  };
  const addCustomHouseRule = () => {
    if (!newHouseRule.trim()) return;
    setFormData(prev => ({ ...prev, customHouseRules: [...prev.customHouseRules, newHouseRule.trim()] }));
    setNewHouseRule('');
  };

  const removeNearbyPlace = (index) => {
    setFormData(prev => ({ ...prev, nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index) }));
  };

  const addTouristPlace = (place) => {
    if (!place.name || place.distance === '' || place.distance == null) return;
    const previewUrl = place.image ? URL.createObjectURL(place.image) : null;
    setFormData(prev => ({
      ...prev,
      touristPlaces: [...prev.touristPlaces, { name: place.name, distance: parseFloat(place.distance) || 0, unit: 'km', description: place.description || '', image: place.image || null, previewUrl }]
    }));
  };

  const removeTouristPlace = (index) => {
    setFormData(prev => {
      const removed = prev.touristPlaces[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return { ...prev, touristPlaces: prev.touristPlaces.filter((_, i) => i !== index) };
    });
  };

  const handleAddProperty = async () => {
    if (!formData.name.trim()) { toast.error('Please enter a property name.'); return; }
    if (!formData.city.trim()) { toast.error('Please enter a city.'); return; }
    if (!formData.pricePerNight || Number(formData.pricePerNight) <= 0) { toast.error('Please enter a valid price per night.'); return; }

    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please login as a host to add a property.'); return; }

    try {
      const submitFormData = new FormData();
      submitFormData.append('name', formData.name);
      submitFormData.append('type', formData.type);
      submitFormData.append('description', formData.description || '');
      submitFormData.append('address', formData.address || '');
      submitFormData.append('city', formData.city);
      submitFormData.append('state', formData.state || '');
      submitFormData.append('pincode', formData.pincode || '');
      submitFormData.append('pricePerNight', formData.pricePerNight);
      submitFormData.append('cleaningFee', formData.cleaningFee || 0);
      submitFormData.append('serviceFeePercent', formData.serviceFeePercent || 10);
      submitFormData.append('securityDeposit', formData.securityDeposit || 0);
      submitFormData.append('bedrooms', toInteger(formData.bedrooms, 1));
      submitFormData.append('bathrooms', toInteger(formData.bathrooms, 1));
      submitFormData.append('beds', toInteger(formData.beds, 1));
      submitFormData.append('maxGuests', toInteger(formData.maxGuests, 2));
      submitFormData.append('propertySizeSqFt', formData.propertySizeSqFt || '');
      submitFormData.append('checkInTime', formData.checkInTime || '3:00 PM - 11:00 PM');
      submitFormData.append('checkOutTime', formData.checkOutTime || '11:00 AM');
      submitFormData.append('minStayNights', formData.minStayNights || 1);
      submitFormData.append('maxStayNights', formData.maxStayNights || '');

      const allAmenities = [...formData.amenities, ...formData.customAmenities];
      submitFormData.append('amenities', JSON.stringify(allAmenities));

      const allHouseRules = [...formData.houseRules, ...formData.customHouseRules];
      submitFormData.append('houseRules', JSON.stringify(allHouseRules));

      formData.images.forEach((image) => { submitFormData.append('images', image); });

      submitFormData.append('nearbyPlaces', JSON.stringify(formData.nearbyPlaces));

      const touristPlacesData = formData.touristPlaces.map(tp => ({
        name: tp.name,
        city: formData.city || '',
        state: formData.state || '',
        distance: tp.distance,
        description: tp.description,
        image: ''
      }));
      submitFormData.append('touristPlaces', JSON.stringify(touristPlacesData));
      formData.touristPlaces.forEach((tp) => { if (tp.image) submitFormData.append('touristPlaceImages', tp.image); });

      submitFormData.append('policies', JSON.stringify({
        cancellationPolicy: formData.cancellationPolicy,
        paymentPolicy: formData.paymentPolicy,
        additionalNotes: formData.additionalNotes,
      }));

      if (formData.discountIsActive) {
        submitFormData.append('discount', JSON.stringify({
          isActive: formData.discountIsActive,
          type: formData.discountType,
          value: parseFloat(formData.discountValue) || 0,
          title: formData.discountTitle,
          description: formData.discountDescription,
          validFrom: formData.discountValidFrom ? new Date(formData.discountValidFrom) : null,
          validUntil: formData.discountValidUntil ? new Date(formData.discountValidUntil) : null,
          minStayNights: formData.discountMinStayNights,
          maxDiscountAmount: formData.discountMaxAmount ? parseFloat(formData.discountMaxAmount) : null,
          badge: formData.discountBadge,
          termsAndConditions: formData.discountTermsConditions,
        }));
      }

      const response = await fetch(getApiUrl('/api/properties/create'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: submitFormData,
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Failed to create property');
      }

      toast.success('Property added successfully!');
      navigate('/host');
    } catch (err) {
      console.error('Add property error:', err);
      toast.error(err.message || 'Failed to add property. Please try again.');
    }
  };

  /* ===== STEP CONTENT RENDERERS ===== */

  const renderStep1 = () => (
    <div className="add-stay-form-section">
      <h3><FaInfoCircle /> Basic Information</h3>
      <div className="add-stay-form-row">
        <div className="add-stay-field">
          <label htmlFor="name">Property Name *</label>
          <input id="name" type="text" value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="e.g. Cozy Beach Villa" />
        </div>
        <div className="add-stay-field">
          <label htmlFor="type">Property Type</label>
          <select id="type" value={formData.type} onChange={e => handleChange('type', e.target.value)}>
            {propertyTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="add-stay-form-row add-stay-form-row-3">
        <div className="add-stay-field">
          <label htmlFor="city">City *</label>
          <input id="city" type="text" value={formData.city} onChange={e => handleChange('city', e.target.value)} placeholder="e.g. Mumbai" />
        </div>
        <div className="add-stay-field">
          <label htmlFor="state">State / Region</label>
          <input id="state" type="text" value={formData.state} onChange={e => handleChange('state', e.target.value)} placeholder="e.g. Maharashtra" />
        </div>
        <div className="add-stay-field">
          <label htmlFor="pincode">Pincode / Zipcode</label>
          <input id="pincode" type="text" value={formData.pincode} onChange={e => handleChange('pincode', e.target.value)} placeholder="e.g. 560001" />
        </div>
      </div>
      <div className="add-stay-field">
        <label htmlFor="address">Address</label>
        <input id="address" type="text" value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="Street address" />
      </div>
      <div className="add-stay-field">
        <div className="description-field-header">
          <label htmlFor="description">Description</label>
          <button
            type="button"
            className="btn-generate-description"
            onClick={handleGenerateDescription}
            disabled={generatingDescription}
          >
            {generatingDescription ? 'Generating…' : '✨ Generate with AI'}
          </button>
        </div>
        <textarea id="description" rows={4} value={formData.description} onChange={e => handleChange('description', e.target.value)} placeholder="Describe your property..." />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="add-stay-form-section">
      <h3><FaBed /> Property Details</h3>
      <div className="add-stay-form-row add-stay-form-row-4">
        <div className="add-stay-field">
          <label htmlFor="bedrooms">Bedrooms</label>
          <input
            id="bedrooms"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={formData.bedrooms}
            onChange={e => handleIntegerInputChange('bedrooms', e.target.value, { max: 50 })}
            onBlur={() => handleIntegerInputBlur('bedrooms', { min: 1, fallback: 1 })}
            placeholder="1"
          />
        </div>
        <div className="add-stay-field">
          <label htmlFor="bathrooms">Bathrooms</label>
          <input
            id="bathrooms"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={formData.bathrooms}
            onChange={e => handleIntegerInputChange('bathrooms', e.target.value, { max: 50 })}
            onBlur={() => handleIntegerInputBlur('bathrooms', { min: 1, fallback: 1 })}
            placeholder="1"
          />
        </div>
        <div className="add-stay-field">
          <label htmlFor="beds">Beds</label>
          <input
            id="beds"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={formData.beds}
            onChange={e => handleIntegerInputChange('beds', e.target.value, { max: 100 })}
            onBlur={() => handleIntegerInputBlur('beds', { min: 1, fallback: 1 })}
            placeholder="1"
          />
        </div>
        <div className="add-stay-field">
          <label htmlFor="maxGuests">Max Guests</label>
          <input
            id="maxGuests"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={formData.maxGuests}
            onChange={e => handleIntegerInputChange('maxGuests', e.target.value, { max: 100 })}
            onBlur={() => handleIntegerInputBlur('maxGuests', { min: 1, fallback: 2 })}
            placeholder="2"
          />
        </div>
      </div>
      <div className="add-stay-field">
        <label htmlFor="propertySizeSqFt">Property Size (sq ft)</label>
        <input id="propertySizeSqFt" type="text" value={formData.propertySizeSqFt} onChange={e => handleChange('propertySizeSqFt', e.target.value)} placeholder="e.g. 1200" />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="add-stay-form-section">
      <h3><FaClock /> Check-in &amp; Stay Rules</h3>
      <div className="add-stay-form-row">
        <div className="add-stay-field">
          <label htmlFor="checkInTime">Check-in Window</label>
          <div className="add-stay-time-dropdown" ref={checkInDropdownRef}>
            <button
              id="checkInTime"
              type="button"
              className="add-stay-time-trigger"
              onClick={() => setOpenTimeDropdown(prev => (prev === 'checkIn' ? '' : 'checkIn'))}
            >
              <span>{formData.checkInTime}</span>
              <FaChevronDown className={`add-stay-time-chevron ${openTimeDropdown === 'checkIn' ? 'open' : ''}`} />
            </button>
            {openTimeDropdown === 'checkIn' && (
              <div className="add-stay-time-menu">
                {CHECK_IN_WINDOWS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    className={`add-stay-time-option ${formData.checkInTime === slot ? 'selected' : ''}`}
                    onClick={() => {
                      handleChange('checkInTime', slot);
                      setOpenTimeDropdown('');
                    }}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="add-stay-field">
          <label htmlFor="checkOutTime">Check-out By</label>
          <div className="add-stay-time-dropdown" ref={checkOutDropdownRef}>
            <button
              id="checkOutTime"
              type="button"
              className="add-stay-time-trigger"
              onClick={() => setOpenTimeDropdown(prev => (prev === 'checkOut' ? '' : 'checkOut'))}
            >
              <span>{formData.checkOutTime}</span>
              <FaChevronDown className={`add-stay-time-chevron ${openTimeDropdown === 'checkOut' ? 'open' : ''}`} />
            </button>
            {openTimeDropdown === 'checkOut' && (
              <div className="add-stay-time-menu">
                {CHECK_OUT_TIMES.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    className={`add-stay-time-option ${formData.checkOutTime === slot ? 'selected' : ''}`}
                    onClick={() => {
                      handleChange('checkOutTime', slot);
                      setOpenTimeDropdown('');
                    }}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="add-stay-form-row">
        <div className="add-stay-field">
          <label htmlFor="minStayNights">Minimum Stay (nights)</label>
          <input id="minStayNights" type="number" min={1} value={formData.minStayNights} onChange={e => handleChange('minStayNights', parseInt(e.target.value, 10) || 1)} />
        </div>
        <div className="add-stay-field">
          <label htmlFor="maxStayNights">Maximum Stay (nights)</label>
          <input id="maxStayNights" type="text" value={formData.maxStayNights} onChange={e => handleChange('maxStayNights', e.target.value)} placeholder="Optional" />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      {/* Pricing */}
      <div className="add-stay-form-section">
        <h3><FaMoneyBillWave /> Pricing</h3>
        <div className="add-stay-form-row add-stay-form-row-4">
          <div className="add-stay-field">
            <label htmlFor="pricePerNight">Price per Night (&#8377;) *</label>
            <input id="pricePerNight" type="number" min={0} value={formData.pricePerNight} onChange={e => handleChange('pricePerNight', e.target.value)} placeholder="e.g. 2500" />
          </div>
          <div className="add-stay-field">
            <label htmlFor="cleaningFee">Cleaning Fee (&#8377;)</label>
            <input id="cleaningFee" type="number" min={0} value={formData.cleaningFee} onChange={e => handleChange('cleaningFee', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="add-stay-field">
            <label htmlFor="serviceFeePercent">Service Fee (%)</label>
            <input id="serviceFeePercent" type="number" min={0} max={100} value={formData.serviceFeePercent} onChange={e => handleChange('serviceFeePercent', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="add-stay-field">
            <label htmlFor="securityDeposit">Security Deposit (&#8377;)</label>
            <input id="securityDeposit" type="number" min={0} value={formData.securityDeposit} onChange={e => handleChange('securityDeposit', parseFloat(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      {/* Amenities */}
      <div className="add-stay-form-section">
        <h3 className="add-stay-collapsible" onClick={() => setShowAmenitiesSection(!showAmenitiesSection)}>
          <span><FaConciergeBell /> Amenities &amp; Facilities</span>
          {showAmenitiesSection ? <FaChevronUp /> : <FaChevronDown />}
        </h3>
        {showAmenitiesSection && (
          <div className="add-stay-amenities-grid">
            {ALL_AMENITIES.map(a => (
              <label key={a} className="add-stay-check-label">
                <input type="checkbox" checked={formData.amenities.includes(a)} onChange={() => toggleAmenity(a)} />
                <span>{a}</span>
              </label>
            ))}
            {formData.customAmenities.map((a, i) => (
              <span key={`c-${i}`} className="add-stay-tag">{a} <button type="button" className="add-stay-tag-remove" onClick={() => setFormData(prev => ({ ...prev, customAmenities: prev.customAmenities.filter((_, j) => j !== i) }))}>x</button></span>
            ))}
            <div className="add-stay-custom-row">
              <input type="text" value={newAmenity} onChange={e => setNewAmenity(e.target.value)} placeholder="Custom amenity" />
              <button type="button" className="add-stay-btn-add" onClick={addCustomAmenity}>Add</button>
            </div>
            {(formData.amenities.length + formData.customAmenities.length) > 0 && (
              <p className="add-stay-count">{formData.amenities.length + formData.customAmenities.length} selected</p>
            )}
          </div>
        )}
      </div>

      {/* Property Images */}
      <div className="add-stay-form-section">
        <h3><FaImage /> Property Images</h3>
        <p className="add-stay-hint">Upload high-quality images to attract guests. First image will be the cover photo.</p>
        <div className="add-stay-images-section">
          {formData.images.length === 0 && (
            <div
              className="add-stay-image-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('dragover');
                const files = Array.from(e.dataTransfer.files);
                const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                const validFiles = files.filter(f => validTypes.includes(f.type));
                if (validFiles.length > 0) handleChange('images', [...formData.images, ...validFiles]);
              }}
            >
              <div className="add-stay-dropzone-icon"><FaCamera /></div>
              <h4>Drag &amp; drop images here</h4>
              <p>or click to browse from your device</p>
              <span className="add-stay-dropzone-formats">JPG, PNG, WEBP supported</span>
            </div>
          )}

          {formData.images.length > 0 && (
            <>
              <div className="add-stay-images-grid">
                {formData.images.map((image, index) => (
                  <div key={index} className={`add-stay-image-card ${index === 0 ? 'cover' : ''}`}>
                    <img src={URL.createObjectURL(image)} alt={`Property ${index + 1}`} />
                    <div className="add-stay-image-overlay">
                      {index === 0 && <span className="add-stay-cover-badge">&#9733; Cover</span>}
                      <span className="add-stay-image-num">{index + 1}</span>
                    </div>
                    <button type="button" className="add-stay-image-remove" onClick={() => handleChange('images', formData.images.filter((_, i) => i !== index))} title="Remove image">
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <div className="add-stay-add-more-card" onClick={() => fileInputRef.current?.click()}>
                  <FaPlus />
                  <span>Add More</span>
                </div>
              </div>
              <div className="add-stay-image-actions">
                <span className="add-stay-image-count-badge">
                  <FaImage /> {formData.images.length} {formData.images.length === 1 ? 'image' : 'images'} selected
                </span>
                <div className="add-stay-upload-btn-group">
                  <button type="button" className="add-stay-upload-action-btn" onClick={() => fileInputRef.current?.click()}>
                    <FaUpload /> Upload Files
                  </button>
                  <button type="button" className="add-stay-upload-action-btn add-stay-camera-btn" onClick={() => cameraInputRef.current?.click()}>
                    <FaCamera /> Camera
                  </button>
                </div>
              </div>
            </>
          )}

          <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple onChange={e => {
            const files = Array.from(e.target.files || []);
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            const validFiles = files.filter(f => validTypes.includes(f.type));
            if (validFiles.length > 0) handleChange('images', [...formData.images, ...validFiles]);
            e.target.value = '';
          }} style={{ display: 'none' }} />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={e => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) handleChange('images', [...formData.images, ...files]);
            e.target.value = '';
          }} style={{ display: 'none' }} />
        </div>
      </div>

      {/* House Rules */}
      <div className="add-stay-form-section">
        <h3 className="add-stay-collapsible" onClick={() => setShowHouseRulesSection(!showHouseRulesSection)}>
          <span><FaScroll /> House Rules</span>
          {showHouseRulesSection ? <FaChevronUp /> : <FaChevronDown />}
        </h3>
        {showHouseRulesSection && (
          <div className="add-stay-house-rules-grid">
            {ALL_HOUSE_RULES.map(r => (
              <label key={r} className="add-stay-check-label">
                <input type="checkbox" checked={formData.houseRules.includes(r)} onChange={() => toggleHouseRule(r)} />
                <span>{r}</span>
              </label>
            ))}
            {formData.customHouseRules.map((r, i) => (
              <span key={`cr-${i}`} className="add-stay-tag">{r} <button type="button" className="add-stay-tag-remove" onClick={() => setFormData(prev => ({ ...prev, customHouseRules: prev.customHouseRules.filter((_, j) => j !== i) }))}>x</button></span>
            ))}
            <div className="add-stay-custom-row">
              <input type="text" value={newHouseRule} onChange={e => setNewHouseRule(e.target.value)} placeholder="Custom house rule" />
              <button type="button" className="add-stay-btn-add" onClick={addCustomHouseRule}>Add</button>
            </div>
            {(formData.houseRules.length + formData.customHouseRules.length) > 0 && (
              <p className="add-stay-count">{formData.houseRules.length + formData.customHouseRules.length} selected</p>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderStep5 = () => (
    <>
      {/* Nearby Places */}
      <div className="add-stay-form-section">
        <h3><FaMapMarkerAlt /> Nearby Places <span className="add-stay-optional-badge">Optional</span></h3>
        <div className="add-stay-place-form">
          <div className="add-stay-form-row add-stay-form-row-4">
            <div className="add-stay-field">
              <label>Name</label>
              <input type="text" value={newNearbyPlace.name} onChange={e => setNewNearbyPlace(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Railway Station" />
            </div>
            <div className="add-stay-field">
              <label>Category</label>
              <select
                value={newNearbyPlace.category}
                onChange={e => setNewNearbyPlace(p => ({ ...p, category: e.target.value }))}
              >
                <option value="">Select category</option>
                {NEARBY_PLACE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="add-stay-field">
              <label>Distance</label>
              <input type="number" min={0} step={0.1} value={newNearbyPlace.distance} onChange={e => setNewNearbyPlace(p => ({ ...p, distance: e.target.value }))} placeholder="e.g. 2.5" />
            </div>
            <div className="add-stay-field">
              <label>Unit</label>
              <select value={newNearbyPlace.unit} onChange={e => setNewNearbyPlace(p => ({ ...p, unit: e.target.value }))}>
                <option value="km">Kilometers</option>
                <option value="m">Meters</option>
              </select>
            </div>
          </div>
          <button type="button" className="add-stay-btn-add" onClick={() => { addNearbyPlace(newNearbyPlace); setNewNearbyPlace({ name: '', category: '', distance: '', unit: 'km' }); }}>
            <FaPlus /> Add Nearby Place
          </button>
        </div>
        {formData.nearbyPlaces.length > 0 && (
          <ul className="add-stay-place-list">
            {formData.nearbyPlaces.map((place, i) => (
              <li key={i} className="add-stay-place-item">
                <span>{place.name}{place.category ? ` (${place.category})` : ''} &#8212; {place.distance} {place.unit}</span>
                <button type="button" className="add-stay-btn-remove" onClick={() => removeNearbyPlace(i)} aria-label="Remove"><FaTrash /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tourist Attractions */}
      <div className="add-stay-form-section">
        <h3><FaLandmark /> Tourist Attractions <span className="add-stay-optional-badge">Optional</span></h3>
        <div className="add-stay-place-form">
          <div className="add-stay-form-row">
            <div className="add-stay-field">
              <label>Tourist Place Name</label>
              <input type="text" value={newTouristPlace.name} onChange={e => setNewTouristPlace(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Beach" />
            </div>
            <div className="add-stay-field">
              <label>Distance (km)</label>
              <input type="number" min={0} step={0.1} value={newTouristPlace.distance} onChange={e => setNewTouristPlace(p => ({ ...p, distance: e.target.value }))} placeholder="e.g. 1.2" />
            </div>
          </div>
          <div className="add-stay-field">
            <label>Description</label>
            <textarea rows={2} value={newTouristPlace.description} onChange={e => setNewTouristPlace(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." />
          </div>
          <div className="add-stay-field">
            <label>Image (optional)</label>
            <div className="add-stay-tourist-image-upload">
              <input ref={touristImageInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={e => {
                const f = e.target.files?.[0];
                if (f) setNewTouristPlace(p => ({ ...p, image: f }));
                e.target.value = '';
              }} />
              {touristImagePreview ? (
                <div className="add-stay-tourist-img-preview">
                  <div className="add-stay-tourist-img-thumb">
                    <img src={touristImagePreview} alt="Preview" />
                  </div>
                  <div className="add-stay-tourist-img-info">
                    <span className="add-stay-tourist-img-name">{newTouristPlace.image?.name}</span>
                    <div className="add-stay-tourist-img-actions">
                      <button type="button" className="add-stay-tourist-img-change" onClick={() => touristImageInputRef.current?.click()}>
                        <FaCamera /> Change
                      </button>
                      <button type="button" className="add-stay-tourist-img-remove" onClick={() => setNewTouristPlace(p => ({ ...p, image: null }))}>
                        <FaTimes /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="add-stay-tourist-img-dropzone" onClick={() => touristImageInputRef.current?.click()}>
                  <FaCamera />
                  <span>Click to upload image</span>
                </div>
              )}
            </div>
          </div>
          <button type="button" className="add-stay-btn-add" onClick={() => { addTouristPlace(newTouristPlace); setNewTouristPlace({ name: '', distance: '', description: '', image: null }); }}>
            <FaPlus /> Add Attraction
          </button>
        </div>
        {formData.touristPlaces.length > 0 && (
          <div className="add-stay-tourist-list">
            {formData.touristPlaces.map((place, i) => (
              <div key={i} className="add-stay-tourist-card">
                {place.previewUrl ? (
                  <div className="add-stay-tourist-card-img">
                    <img src={place.previewUrl} alt={place.name} />
                  </div>
                ) : (
                  <div className="add-stay-tourist-card-img add-stay-tourist-card-no-img">
                    <FaLandmark />
                  </div>
                )}
                <div className="add-stay-tourist-card-info">
                  <h4>{place.name}</h4>
                  <span className="add-stay-tourist-card-dist"><FaMapMarkerAlt /> {place.distance} km away</span>
                  {place.description && <p>{place.description}</p>}
                </div>
                <button type="button" className="add-stay-btn-remove" onClick={() => removeTouristPlace(i)} aria-label="Remove"><FaTrash /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderStep6 = () => (
    <div className="add-stay-form-section">
      <h3><FaFileContract /> Property Policies</h3>
      <div className="add-stay-form-row">
        <div className="add-stay-field">
          <label htmlFor="cancellationPolicy">Cancellation Policy</label>
          <select
            id="cancellationPolicy"
            value={formData.cancellationPolicy}
            onChange={e => handleChange('cancellationPolicy', e.target.value)}
          >
            {CANCELLATION_POLICY_OPTIONS.map((policy) => (
              <option key={policy} value={policy}>{policy}</option>
            ))}
          </select>
        </div>
        <div className="add-stay-field">
          <label htmlFor="paymentPolicy">Payment Policy</label>
          <select
            id="paymentPolicy"
            value={formData.paymentPolicy}
            onChange={e => handleChange('paymentPolicy', e.target.value)}
          >
            {PAYMENT_POLICY_OPTIONS.map((policy) => (
              <option key={policy} value={policy}>{policy}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="add-stay-field">
        <label htmlFor="additionalNotes">Additional Policy Notes (Optional)</label>
        <textarea id="additionalNotes" rows={3} value={formData.additionalNotes} onChange={e => handleChange('additionalNotes', e.target.value)} placeholder="Any other policies or notes" />
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="add-stay-form-section">
      <h3><FaTags /> Exclusive Offers &amp; Discounts <span className="add-stay-optional-badge">Optional</span></h3>
      <div className="add-stay-field add-stay-checkbox-row">
        <input id="discountIsActive" type="checkbox" checked={formData.discountIsActive} onChange={e => handleChange('discountIsActive', e.target.checked)} />
        <label htmlFor="discountIsActive">Enable Special Offer / Discount</label>
      </div>

      {formData.discountIsActive && (
        <div className="add-stay-discount-fields">
          <div className="add-stay-form-row add-stay-form-row-3">
            <div className="add-stay-field">
              <label>Discount Type</label>
              <select value={formData.discountType} onChange={e => handleChange('discountType', e.target.value)}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div className="add-stay-field">
              <label>Discount Value</label>
              <input type="text" value={formData.discountValue} onChange={e => handleChange('discountValue', e.target.value)} placeholder="e.g. 10 or 500" />
            </div>
            <div className="add-stay-field">
              <label>Display Badge</label>
              <input type="text" value={formData.discountBadge} onChange={e => handleChange('discountBadge', e.target.value)} placeholder="e.g. Special Offer" />
            </div>
          </div>
          <div className="add-stay-form-row">
            <div className="add-stay-field">
              <label>Offer Title</label>
              <input type="text" value={formData.discountTitle} onChange={e => handleChange('discountTitle', e.target.value)} placeholder="e.g. Early Bird" />
            </div>
            <div className="add-stay-field">
              <label>Offer Description</label>
              <textarea rows={2} value={formData.discountDescription} onChange={e => handleChange('discountDescription', e.target.value)} placeholder="Describe this offer..." />
            </div>
          </div>
          <div className="add-stay-form-row add-stay-form-row-4">
            <div className="add-stay-field">
              <label>Valid From</label>
              <DatePicker
                selected={formData.discountValidFrom ? new Date(formData.discountValidFrom) : null}
                onChange={(date) => handleChange('discountValidFrom', date ? date.toISOString().slice(0, 10) : '')}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                className="add-stay-date-input"
                wrapperClassName="add-stay-date-wrapper"
                popperClassName="add-stay-datepicker-popper"
                popperPlacement="bottom-start"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                calendarClassName="add-stay-calendar"
              />
            </div>
            <div className="add-stay-field">
              <label>Valid Until</label>
              <DatePicker
                selected={formData.discountValidUntil ? new Date(formData.discountValidUntil) : null}
                onChange={(date) => handleChange('discountValidUntil', date ? date.toISOString().slice(0, 10) : '')}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                className="add-stay-date-input"
                wrapperClassName="add-stay-date-wrapper"
                popperClassName="add-stay-datepicker-popper"
                popperPlacement="bottom-start"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                calendarClassName="add-stay-calendar"
              />
            </div>
            <div className="add-stay-field">
              <label>Min Stay (Nights)</label>
              <input type="number" min={1} value={formData.discountMinStayNights} onChange={e => handleChange('discountMinStayNights', parseInt(e.target.value, 10) || 1)} />
            </div>
            <div className="add-stay-field">
              <label>Max Discount (&#8377;)</label>
              <input type="text" value={formData.discountMaxAmount} onChange={e => handleChange('discountMaxAmount', e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <div className="add-stay-field">
            <label>Terms &amp; Conditions</label>
            <textarea rows={3} value={formData.discountTermsConditions} onChange={e => handleChange('discountTermsConditions', e.target.value)} placeholder="Terms for this offer..." />
          </div>
        </div>
      )}
    </div>
  );

  const stepRenderers = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6, renderStep7];

  /* ===== FOOTER BUTTONS ===== */
  const renderFooter = () => {
    // Step 6 (Policies) -- special: Add Property + Offers button
    if (activeStep === 5) {
      return (
        <div className="add-stay-modal-actions">
          <button type="button" className="add-stay-btn-prev" onClick={goPrev}>
            <FaChevronLeft /> Previous
          </button>
          <div className="add-stay-nav-btns">
            <button type="button" className="add-stay-btn-create" onClick={handleAddProperty}>
              <FaCheck /> Add Property
            </button>
            <button type="button" className="add-stay-btn-next add-stay-btn-offers" onClick={goNext}>
              <FaTags /> Offers &amp; Discounts
            </button>
          </div>
        </div>
      );
    }

    // Step 7 (Offers) -- final: Save Offer
    if (activeStep === 6) {
      return (
        <div className="add-stay-modal-actions">
          <button type="button" className="add-stay-btn-prev" onClick={goPrev}>
            <FaChevronLeft /> Previous
          </button>
          <div className="add-stay-nav-btns">
            <button type="button" className="add-stay-btn-create" onClick={handleAddProperty}>
              <FaCheck /> Save &amp; Add Property
            </button>
          </div>
        </div>
      );
    }

    // Steps 1-5
    return (
      <div className="add-stay-modal-actions">
        <button type="button" className="add-stay-btn-cancel" onClick={() => navigate('/host')}>
          Cancel
        </button>
        <div className="add-stay-nav-btns">
          {activeStep > 0 && (
            <button type="button" className="add-stay-btn-prev" onClick={goPrev}>
              <FaChevronLeft /> Previous
            </button>
          )}
          <button type="button" className="add-stay-btn-next" onClick={goNext}>
            Next <FaChevronRight />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="add-stay-property-page">
      <main className="add-stay-property-main">
        <div className="add-stay-property-modal">
          {/* Gradient Header */}
          <header className="add-stay-modal-header">
            <h2>Add New Property</h2>
            <div className="add-stay-header-right">
              <span className="add-stay-step-indicator">Step {activeStep + 1} of {STEPS.length}</span>
              <button type="button" className="add-stay-close-btn" onClick={() => navigate('/host')} aria-label="Close">
                <FaTimes />
              </button>
            </div>
          </header>

          {/* Step Progress Bar */}
          <div className="add-stay-step-bar-wrapper">
            <div className="add-stay-step-bar" ref={stepBarRef}>
              {STEPS.map((step, i) => (
                <button
                  key={i}
                  type="button"
                  className={`add-stay-step${i === activeStep ? ' active' : ''}${i < activeStep ? ' completed' : ''}`}
                  onClick={() => goToStep(i)}
                >
                  <span className="add-stay-step-circle">
                    {i < activeStep ? <FaCheck /> : step.icon}
                  </span>
                  <span className="add-stay-step-label">{step.label}</span>
                  {i < STEPS.length - 1 && <span className="add-stay-step-connector" />}
                </button>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="add-stay-modal-body">
            {stepRenderers[activeStep]()}
          </div>

          {/* Footer */}
          {renderFooter()}
        </div>
      </main>
    </div>
  );
};

export default AddStayPropertyPage;
